import { pdsTv } from '@purinanbm/pds-ui';
import { useLocation } from '@reach/router';
import { sendIt } from 'gatsby-plugin-purina-analytics/common/functions';
import React from 'react';
import { CMS_OVERLAY, CMS_THEME } from '../../common/enums';
import { CardLink } from '../Link';

interface Props {
  grid?: boolean;
  imageComponent: JSX.Element;
  condensed: boolean;
  drupal_id: string;
  title: string;
  url: string;
  summary?: string;
  cmsOverlay?: CMS_OVERLAY;
  cmsTheme?: CMS_THEME;
  type: string;
  name: string;
  cardType?: 'article_card' | 'related_article_component';
  onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void; // TODO: implement onClick for article pages & article cards
}

const overlayStyles = pdsTv({
  slots: {
    articleImage: 'pds-full pds-overflow-hidden pds-rounded',
    link: ['pds-text-title-md pds-no-underline after:pds-content-empty'],
    card: 'pds-relative pds-flex pds-w-full pds-flex-col pds-gap-4 pds-overflow-clip md:pds-gap-4.5',
  },
  variants: {
    condensed: {
      true: {
        card: 'lg:pds-flex-row lg:pds-items-start',
        articleImage:
          'lg:pds-h-[234px] lg:pds-w-full lg:pds-min-w-[50%] lg:pds-max-w-[350px] lg:pds-max-w-[60%] lg:pds-grow',
      },
      false: {
        articleImage: '*:pds-h-[inherit] lg:pds-h-[492px]',
      },
    },
    grid: {
      true: {},
      false: {},
    },
  },
  // for 2 card only variations
  compoundVariants: [
    {
      condensed: false,
      grid: false,
      class: { articleImage: 'lg:pds-h-[inherit]' },
    },
  ],
});

const ArticleOverlayCard: React.FC<Props> = function ({
  title,
  url,
  grid = true,
  imageComponent,
  condensed,
  cardType = 'article_card',
  cmsTheme = CMS_THEME.LIGHT,
  onClick = () => {},
  ...props
}: Props) {
  const location = useLocation();
  const styles = React.useMemo(
    () =>
      overlayStyles({
        condensed,
        grid,
      }),
    [condensed, grid],
  );

  const { link, card, articleImage } = styles;
  const handleGA = () => {
    sendIt({
      event: 'select_content',
      eventParams: {
        content_name: `article: ${title}`,
        content_type: `article: ${cardType}`,
        cta_location: 'article',
        link_url: `${url} - ${location.hostname}${location.pathname}`,
      },
    });
  };

  return (
    <article className={card()} {...props}>
      <div className={articleImage()}>{imageComponent}</div>
      <div className={`${condensed ? 'typography_h4' : 'typography_h3'} pds-w-full`}>
        <CardLink
          onClick={e => {
            handleGA();
            onClick(e);
          }}
          analytics={false}
          theme={cmsTheme}
          className={link()}
          to={url}
        >
          {title}
        </CardLink>
      </div>
    </article>
  );
};

export default ArticleOverlayCard;
